import { useEffect, useState } from "react";
import { apiGetPropertyAssetStateCounts } from "../../graphQL/properties/collections/propertyAssets";

/**
 * Fetch total count of propertyAssets per state value, scoped by domain.
 * Returns a map { state: count }.
 *
 * Uses the single propertyAssetStateCounts query (one $group server-side)
 * instead of one list query per state. The server groups on EXACT stored
 * state values while the UI option list may differ in case, so counts are
 * folded case-insensitively before lookup.
 *
 * Returns null while counts are loading (or when no domain is active, in
 * which case callers should show all options). Once loaded, callers can use
 * the map to hide zero-count filter options from the UI.
 */
export const usePropertyStateCounts = (states, baseFilters = {}) => {
  const [counts, setCounts] = useState(null);
  const statesKey = (states || []).join("|");
  const domain = String(baseFilters?.domain || "").trim();
  const authorId = String(baseFilters?.authorId || "").trim();

  useEffect(() => {
    if (!Array.isArray(states) || states.length === 0) {
      setCounts({});
      return undefined;
    }
    if (!domain) {
      // Backend query requires a domain; without one (super-admin "All"),
      // leave counts unknown so every state option stays visible.
      setCounts(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await apiGetPropertyAssetStateCounts({
          domain,
          ...(authorId ? { authorId } : {}),
        });
        if (cancelled) return;
        // Fold server rows (exact stored values) case-insensitively.
        const byLower = new Map();
        for (const row of rows) {
          const key = String(row?.state || "").trim().toLowerCase();
          if (!key) continue;
          byLower.set(key, (byLower.get(key) || 0) + (Number(row?.count) || 0));
        }
        const map = {};
        for (const s of states) {
          map[s] = byLower.get(String(s).trim().toLowerCase()) || 0;
        }
        setCounts(map);
      } catch (e) {
        if (!cancelled) {
          console.error("usePropertyStateCounts failed:", e);
          setCounts({});
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statesKey, domain, authorId]);

  return counts;
};
